/** Screener overlay: run advisor agents and mark matching rows. */

import {
  fetchLatestAdvice,
  runAgentAdvice,
  updateAdviceStatus,
  type AdvisorRecommendation,
  type LatestAdviceResponse,
  type PaperAgentMeta,
} from "../api/agentsPaperClient";
import { choiceDialog, contentDialog } from "./confirmDialog";
import { escapeDataTipHtml, formatAgentJustificationHtml } from "../shared/formatDataTip";
import { buildOccSymbol } from "./wheelActions";

export type AdviceScreenerPage = "puts" | "calls" | "options";

/** Fired when GET /advice cache changes so Puts/Calls can pin advised rows through filters. */
export const ADVICE_UPDATED_EVENT = "sfe-advice-updated";

const ATTR_UNDERLYING = "data-underlying";
const ATTR_SIDE = "data-side";
const ATTR_STRIKE = "data-strike";
const ATTR_EXP = "data-exp";
const ATTR_OCC = "data-option-symbol";
const PACIFIC_TZ = "America/Los_Angeles";

let cachedRecs: AdvisorRecommendation[] = [];
let cachedUpdatedAt: string | null = null;
let lastAdviceSig = "";
let advisorEnabledKnown = false;
let cachedAdvisorEnabled = false;
let agentStatusListenerBound = false;

const ADVISORY_VISIBLE_KEY = "sfe-advisory-visible";
let advisoryVisible: boolean | null = null;

/** Advisory switch: whether advisor rows/dots are overlaid on screener tabs. Defaults on. */
export function isAdvisoryVisible(): boolean {
  if (advisoryVisible === null) {
    try {
      advisoryVisible = window.localStorage.getItem(ADVISORY_VISIBLE_KEY) !== "0";
    } catch {
      advisoryVisible = true;
    }
  }
  return advisoryVisible;
}

function setAdvisoryVisible(next: boolean): void {
  advisoryVisible = next;
  try {
    window.localStorage.setItem(ADVISORY_VISIBLE_KEY, next ? "1" : "0");
  } catch {
    /* private mode: keep the choice in memory only */
  }
}

type BoundPanel = { root: HTMLElement; page: AdviceScreenerPage };
const boundPanels: BoundPanel[] = [];

function escAttr(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function normalizeOcc(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function normalizeTickerKey(value: string | null | undefined): string {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "");
}

function normalizeSide(value: string | null | undefined): "call" | "put" | "" {
  const s = String(value || "").trim().toLowerCase();
  if (s.startsWith("c")) return "call";
  if (s.startsWith("p")) return "put";
  return "";
}

function normalizeExp(value: string | null | undefined): string {
  const raw = String(value || "").trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  if (/^\d{8}$/.test(raw)) {
    return `${raw.slice(0, 4)}-${raw.slice(4, 6)}-${raw.slice(6, 8)}`;
  }
  const us = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (us) {
    return `${us[3]}-${us[1].padStart(2, "0")}-${us[2].padStart(2, "0")}`;
  }
  return "";
}

function parseOcc(symbol: string | null | undefined): {
  ticker: string;
  expirationDate: string;
  side: "call" | "put";
  strike: number;
} | null {
  const raw = normalizeOcc(symbol);
  const match = raw.match(/^([A-Z]+)(\d{6})([CP])(\d{8})$/);
  if (!match) return null;
  const [, ticker, yymmdd, cp, strikeMills] = match;
  const yy = Number(yymmdd.slice(0, 2));
  const mm = Number(yymmdd.slice(2, 4));
  const dd = Number(yymmdd.slice(4, 6));
  if (!yy || !mm || !dd) return null;
  const year = yy >= 70 ? 1900 + yy : 2000 + yy;
  const strike = Number(strikeMills) / 1000;
  if (!Number.isFinite(strike) || strike <= 0) return null;
  return {
    ticker,
    expirationDate: `${year}-${String(mm).padStart(2, "0")}-${String(dd).padStart(2, "0")}`,
    side: cp === "C" ? "call" : "put",
    strike,
  };
}

type AdviceRowKey = {
  underlying?: string | null;
  side?: string | null;
  strike?: number | string | null;
  expiration?: string | null;
  optionSymbol?: string | null;
};

function recFields(rec: AdvisorRecommendation): {
  und: string;
  side: "call" | "put" | "";
  strike: number;
  exp: string;
  occ: string;
} {
  const parsed = parseOcc(rec.optionSymbol);
  const side = normalizeSide(rec.side) || parsed?.side || "";
  const und = normalizeTickerKey(rec.underlying) || parsed?.ticker || "";
  const strikeRaw = Number(rec.strike);
  const strike = Number.isFinite(strikeRaw) && strikeRaw > 0 ? strikeRaw : parsed?.strike || 0;
  const exp = normalizeExp(rec.expirationDate) || parsed?.expirationDate || "";
  const occ =
    normalizeOcc(rec.optionSymbol) ||
    normalizeOcc(buildOccSymbol(und || rec.underlying, exp, side, strike) || "");
  return { und, side, strike, exp, occ };
}

function recOcc(rec: AdvisorRecommendation): string {
  return recFields(rec).occ;
}

function recMatchesPage(rec: AdvisorRecommendation, page: AdviceScreenerPage): boolean {
  const action = String(rec.action || "").toLowerCase();
  const side = recFields(rec).side;
  if (page === "puts") return action === "open" && side !== "call";
  if (page === "calls") return action === "open" && side !== "put";
  return action === "close" || action === "hold" || action === "skip";
}

function attrsMatchRec(key: AdviceRowKey, rec: AdvisorRecommendation): boolean {
  const rf = recFields(rec);
  const rowParsed = parseOcc(key.optionSymbol);
  const rowOcc = normalizeOcc(key.optionSymbol);
  const rowUnd = normalizeTickerKey(key.underlying) || rowParsed?.ticker || "";
  const rowSide = normalizeSide(key.side) || rowParsed?.side || "";
  const rowStrikeRaw = Number(key.strike);
  const rowStrike =
    Number.isFinite(rowStrikeRaw) && rowStrikeRaw > 0 ? rowStrikeRaw : rowParsed?.strike || 0;
  const rowExp = normalizeExp(key.expiration) || rowParsed?.expirationDate || "";

  if (rf.occ && rowOcc && rf.occ === rowOcc) return true;
  if (!rf.und || !rowUnd || rf.und !== rowUnd) return false;
  if (rf.side && rowSide && rf.side !== rowSide) return false;
  if (rf.strike > 0 && rowStrike > 0 && Math.abs(rf.strike - rowStrike) > 0.001) return false;
  if (rf.exp && rowExp && rf.exp !== rowExp) return false;
  return Boolean(rf.side || rf.occ || (rf.strike > 0 && rf.exp) || rowSide || rowOcc);
}

function rowMatchesRec(row: HTMLElement, rec: AdvisorRecommendation): boolean {
  return attrsMatchRec(
    {
      underlying: row.getAttribute(ATTR_UNDERLYING),
      side: row.getAttribute(ATTR_SIDE),
      strike: row.getAttribute(ATTR_STRIKE),
      expiration: row.getAttribute(ATTR_EXP),
      optionSymbol: row.getAttribute(ATTR_OCC),
    },
    rec,
  );
}

function calendarDayPt(ms: number): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: PACIFIC_TZ,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(ms));
}

function shiftYmd(ymd: string, days: number): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, (m || 1) - 1, (d || 1) + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

/** Compact PT stamp, e.g. "today, 1.20 pm" / "yesterday, 9.05 am" / "Aug 12, 1.20 pm". */
function fmtAdviceWhen(value: string | null | undefined, nowMs = Date.now()): string {
  const ms = Date.parse(String(value || ""));
  if (!Number.isFinite(ms)) return "";
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PACIFIC_TZ,
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).formatToParts(new Date(ms));
  const get = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value || "";
  const hour = get("hour");
  const minute = get("minute");
  const period = get("dayPeriod").replace(/\./g, "").toLowerCase();
  const time = `${hour}.${minute} ${period}`;
  const day = calendarDayPt(ms);
  const today = calendarDayPt(nowMs);
  if (day === today) return `today, ${time}`;
  if (day === shiftYmd(today, -1)) return `yesterday, ${time}`;
  const month = get("month");
  const date = get("day");
  const year = get("year");
  if (year === today.slice(0, 4)) {
    return `${month} ${date}, ${time}`;
  }
  return `${month} ${date}, ${year}, ${time}`;
}

function isPendingAdvice(rec: AdvisorRecommendation): boolean {
  const status = String(rec.userStatus ?? "pending").toLowerCase();
  return status !== "accepted" && status !== "dismissed";
}

function pendingAdviceRecs(recs: AdvisorRecommendation[]): AdvisorRecommendation[] {
  return recs.filter((rec) => {
    if (!isPendingAdvice(rec)) return false;
    const action = String(rec.action || "").toLowerCase();
    if (action === "open" && !(Number(rec.premium) > 0)) return false;
    return true;
  });
}

/** Screener-facing recs. Empty while the Advisory switch is off, which hides
 * dots, badges, pinned rows and synthetic advisor lines in one place. */
function visibleAdviceRecs(recs: AdvisorRecommendation[]): AdvisorRecommendation[] {
  return isAdvisoryVisible() ? pendingAdviceRecs(recs) : [];
}

function recContractLabel(rec: AdvisorRecommendation): string {
  const { und, side, strike, exp } = recFields(rec);
  const sideLabel = side === "call" ? "CALL" : side === "put" ? "PUT" : "";
  const strikeText = strike > 0
    ? (Number.isInteger(strike) ? String(strike) : strike.toFixed(2))
    : "";
  return [und, strikeText, sideLabel, exp].filter(Boolean).join(" · ");
}

function adviceModalFooterHtml(recs: AdvisorRecommendation[]): string {
  const pending = pendingAdviceRecs(recs);
  if (!pending.length) return "";
  if (pending.length === 1) {
    const rec = pending[0];
    return `<button type="button" class="sfe-btn" data-sfe-advice-action="accept" data-rec-id="${escAttr(rec.id)}" data-agent-id="${escAttr(String(rec.agentId ?? ""))}">Accept</button>
      <button type="button" class="sfe-btn sfe-btn-ghost" data-sfe-advice-action="dismiss" data-rec-id="${escAttr(rec.id)}" data-agent-id="${escAttr(String(rec.agentId ?? ""))}">Dismiss</button>`;
  }
  return `<button type="button" class="sfe-btn sfe-btn-ghost" data-sfe-advice-action="accept-all">Accept all</button>
    <button type="button" class="sfe-btn sfe-btn-ghost" data-sfe-advice-action="dismiss-all">Dismiss all</button>`;
}

/** Optional free-text comment the user typed for one recommendation. */
function readAdviceNote(scope: Element | null | undefined): string {
  const input = scope?.querySelector<HTMLTextAreaElement>("[data-sfe-advice-note]");
  return (input?.value || "").trim().slice(0, 500);
}

function collectAdviceNotes(modal: Element | null | undefined): Record<string, string> {
  const notes: Record<string, string> = {};
  const articles = modal?.querySelectorAll<HTMLElement>(".sfe-advice-modal-rec") ?? [];
  for (const article of articles) {
    const recId = article.dataset.recId || "";
    const note = readAdviceNote(article);
    if (recId && note) notes[recId] = note;
  }
  return notes;
}

async function handleAdviceDispositionClick(
  action: string,
  recId: string,
  agentId: string,
  button: HTMLElement,
  pending: AdvisorRecommendation[],
  onUpdated?: () => void,
): Promise<boolean> {
  const modalRoot = button.closest(".sfe-modal");
  if (action === "accept-all") {
    await applyAdviceDisposition(pending, "accepted", collectAdviceNotes(modalRoot));
    onUpdated?.();
    return true;
  }
  if (action === "dismiss-all") {
    await applyAdviceDisposition(pending, "dismissed", collectAdviceNotes(modalRoot));
    onUpdated?.();
    return true;
  }
  const status = action === "accept" ? "accepted" : action === "dismiss" ? "dismissed" : null;
  if (!status || !recId || !agentId) return false;
  if (button instanceof HTMLButtonElement) button.disabled = true;
  // Footer buttons sit outside the rec article, so fall back to a lookup by id.
  const article =
    button.closest<HTMLElement>(".sfe-advice-modal-rec") ??
    [...(modalRoot?.querySelectorAll<HTMLElement>(".sfe-advice-modal-rec") ?? [])].find(
      (el) => el.dataset.recId === recId,
    ) ??
    null;
  await updateAdviceStatus(status, [recId], agentId, readAdviceNote(article) || undefined);
  await refreshCachedAdvice();
  refreshAllBoundPanels();
  onUpdated?.();
  button.closest(".sfe-advice-modal-rec")?.remove();
  const modal = button.closest(".sfe-content-dialog-body");
  return !modal?.querySelector(".sfe-advice-modal-rec");
}

/** Advice detail modal with Accept / Dismiss — used by screeners and Agentic tab. */
export function openAdviceDetailModal(
  recs: AdvisorRecommendation[],
  opts?: { onUpdated?: () => void },
): void {
  const pending = pendingAdviceRecs(recs);
  if (!pending.length) return;
  const und = String(pending[0]?.underlying || "").trim().toUpperCase();
  const showPerRecActions = pending.length > 1;
  void contentDialog({
    title: und ? `Advice · ${und}` : "Agent advice",
    bodyHtml: adviceModalHtml(pending, showPerRecActions),
    closeLabel: "Close",
    modalClass: "sfe-advice-modal",
    footerHtml: adviceModalFooterHtml(pending),
    onBodyAction: async (action, recId, agentId, button) =>
      handleAdviceDispositionClick(action, recId, agentId, button, pending, opts?.onUpdated),
    onFooterAction: async (action, button) => {
      const recId = button.dataset.recId || "";
      const agentId = button.dataset.agentId || "";
      if (recId && agentId) {
        return handleAdviceDispositionClick(action, recId, agentId, button, pending, opts?.onUpdated);
      }
      return handleAdviceDispositionClick(action, "", "", button, pending, opts?.onUpdated);
    },
  });
}

function adviceModalHtml(recs: AdvisorRecommendation[], showActions = true): string {
  return recs
    .map((rec) => {
      const action = String(rec.action || "open").toUpperCase();
      const qty = rec.quantity > 1 ? ` ×${rec.quantity}` : "";
      const when = fmtAdviceWhen(rec.ts || cachedUpdatedAt);
      const why = (rec.justification || rec.summary || rec.reason || "").trim();
      const whyHtml = why
        ? /\sCursor:\s/.test(why)
          ? formatAgentJustificationHtml(why)
          : `<p class="sfe-agent-why-prose">${escapeDataTipHtml(why)}</p>`
        : `<p class="sfe-agent-why-prose">No justification provided.</p>`;
      return `<article class="sfe-advice-modal-rec" data-rec-id="${escAttr(rec.id)}" data-agent-id="${escAttr(String(rec.agentId ?? ""))}">
        <header class="sfe-advice-modal-head">
          <span class="sfe-advice-modal-action">${escapeDataTipHtml(action)}${qty}</span>
          ${rec.agentName ? `<span class="sfe-advice-modal-agent">${escapeDataTipHtml(rec.agentName)}</span>` : ""}
          ${when ? `<span class="sfe-advice-modal-when">${escapeDataTipHtml(when)}</span>` : ""}
        </header>
        <p class="sfe-advice-modal-contract">${escapeDataTipHtml(recContractLabel(rec))}</p>
        ${whyHtml}
        <label class="sfe-advice-note-field">
          <span class="sfe-advice-note-label">Comment for the advisor (optional)</span>
          <textarea class="sfe-advice-note-input" data-sfe-advice-note rows="2" maxlength="500"
            placeholder="Sent to the advisor with your decision on the next cycle"></textarea>
        </label>
        ${
          showActions
            ? `<div class="sfe-advice-modal-actions">
          <button type="button" class="small" data-sfe-advice-action="accept" data-rec-id="${escAttr(rec.id)}" data-agent-id="${escAttr(String(rec.agentId ?? ""))}">Accept</button>
          <button type="button" class="small ghost" data-sfe-advice-action="dismiss" data-rec-id="${escAttr(rec.id)}" data-agent-id="${escAttr(String(rec.agentId ?? ""))}">Dismiss</button>
        </div>`
            : ""
        }
      </article>`;
    })
    .join("");
}

async function applyAdviceDisposition(
  recs: AdvisorRecommendation[],
  status: "accepted" | "dismissed",
  notesByRecId: Record<string, string> = {},
): Promise<void> {
  const pending = pendingAdviceRecs(recs);
  if (!pending.length) return;
  // Group by agent *and* comment so per-rec notes survive Accept all / Dismiss all.
  const groups = new Map<string, { agentId: string; note: string; recIds: string[] }>();
  for (const rec of pending) {
    const agentId = String(rec.agentId ?? "").trim();
    if (!agentId || !rec.id) continue;
    const note = notesByRecId[rec.id] ?? "";
    const key = `${agentId}\n${note}`;
    const group = groups.get(key) ?? { agentId, note, recIds: [] };
    group.recIds.push(rec.id);
    groups.set(key, group);
  }
  for (const group of groups.values()) {
    await updateAdviceStatus(status, group.recIds, group.agentId, group.note || undefined);
  }
  await refreshCachedAdvice();
  refreshAllBoundPanels();
}

function openAdviceModal(recs: AdvisorRecommendation[]): void {
  openAdviceDetailModal(recs);
}

function pickLatestAdviceAt(latest: LatestAdviceResponse | null): string | null {
  const times = [latest?.updatedAt, ...(latest?.agents || []).map((a) => a.lastAdviceAt)].filter(
    (v): v is string => Boolean(v && String(v).trim()),
  );
  if (!times.length) return cachedUpdatedAt;
  return times.sort()[times.length - 1] || cachedUpdatedAt;
}

export function screenerAdviceButtonHtml(): string {
  return `<span class="sfe-agent-advice-host">
    <button type="button" class="small sfe-agent-advice-btn" data-sfe-run-advice aria-label="Run agent advice" hidden>
      <span class="sfe-agent-advice-label" data-sfe-advice-label>Run agent advice</span>
      <span class="sfe-agent-advice-meta" data-sfe-advice-meta hidden></span>
    </button>
  </span>`;
}

/** Advisory on/off chip — drop into a screener filter row beside the other chips. */
export function advisoryFilterFieldHtml(): string {
  const on = isAdvisoryVisible();
  return `<div class="filter-field">
        <label>Advisory<span class="hint" tabindex="0" role="img" data-tip="Show advisor recommendations in this table: advised rows, dots and advice-only lines.">i</span></label>
        <div class="year-chips">
          <button type="button" class="year-chip${on ? " active" : ""}" data-sfe-advisory-toggle aria-pressed="${on ? "true" : "false"}">Show advised</button>
        </div>
      </div>`;
}

function paintAdvisoryToggle(root: HTMLElement, page: AdviceScreenerPage): void {
  const on = isAdvisoryVisible();
  const n = pagePendingAdviceCount(page);
  const label = isAdviceRunAvailable() && n ? `Show advised · ${n}` : "Show advised";
  for (const chip of root.querySelectorAll<HTMLElement>("[data-sfe-advisory-toggle]")) {
    chip.classList.toggle("active", on);
    chip.setAttribute("aria-pressed", on ? "true" : "false");
    chip.textContent = label;
  }
}

/** Stable key matching `data-option-symbol` on screener rows. */
export function screenerContractKey(opts: {
  underlying?: string | null;
  side?: string | null;
  strike?: number | null;
  expiration?: string | null;
  optionSymbol?: string | null;
}): string {
  const und = String(opts.underlying || "")
    .trim()
    .toUpperCase();
  const side = normalizeSide(opts.side);
  const exp = String(opts.expiration || "").slice(0, 10);
  const strike = Number(opts.strike);
  const occ =
    normalizeOcc(opts.optionSymbol) ||
    normalizeOcc(buildOccSymbol(und, exp, side, Number.isFinite(strike) ? strike : 0) || "");
  if (occ) return occ;
  if (und && exp) return `${und}|${side || "put"}|${Number.isFinite(strike) ? strike : ""}|${exp}`;
  return "";
}

export function adviceRowAttrs(opts: {
  underlying: string;
  side: string;
  strike: number;
  expiration: string;
  optionSymbol?: string;
}): string {
  const und = String(opts.underlying || "")
    .trim()
    .toUpperCase();
  const side = normalizeSide(opts.side);
  const exp = String(opts.expiration || "").slice(0, 10);
  const occ =
    normalizeOcc(opts.optionSymbol) ||
    normalizeOcc(buildOccSymbol(und, exp, side, opts.strike) || "");
  const parts = [
    und ? `${ATTR_UNDERLYING}="${escAttr(und)}"` : "",
    side ? `${ATTR_SIDE}="${escAttr(side)}"` : "",
    Number.isFinite(opts.strike) ? `${ATTR_STRIKE}="${escAttr(String(opts.strike))}"` : "",
    exp ? `${ATTR_EXP}="${escAttr(exp)}"` : "",
    occ ? `${ATTR_OCC}="${escAttr(occ)}"` : "",
  ];
  return parts.filter(Boolean).join(" ");
}

function tickerCellHost(row: HTMLElement): HTMLElement | null {
  return (
    row.querySelector<HTMLElement>(".sfe-options-ticker-cell") ||
    row.querySelector<HTMLElement>("td.tk-cell") ||
    row.querySelector<HTMLElement>('td[data-col="ticker"], td[data-col="underlying_ticker"]')
  );
}

function clearAdviceDot(row: HTMLElement): void {
  row.classList.remove("sfe-row-advised");
  row.removeAttribute("data-tip");
  row.removeAttribute("title");
  row.querySelectorAll(".sfe-advice-dot").forEach((el) => el.remove());
  row.querySelectorAll(".sfe-advice-dot-host").forEach((el) => el.classList.remove("sfe-advice-dot-host"));
}

function placeAdviceDot(row: HTMLElement): void {
  const host = tickerCellHost(row);
  if (!host) return;
  host.classList.add("sfe-advice-dot-host");
  let dot = host.querySelector<HTMLButtonElement>(":scope > .sfe-advice-dot");
  if (!dot) {
    dot = document.createElement("button");
    dot.type = "button";
    dot.className = "sfe-advice-dot";
    host.appendChild(dot);
  }
  dot.setAttribute("aria-label", "Show agent advice");
  dot.title = "Show agent advice";
}

function pageAdviceRecs(page: AdviceScreenerPage): AdvisorRecommendation[] {
  return visibleAdviceRecs(cachedRecs).filter((rec) => recMatchesPage(rec, page));
}

function pagePendingAdviceCount(page: AdviceScreenerPage): number {
  return pendingAdviceRecs(cachedRecs).filter((rec) => recMatchesPage(rec, page)).length;
}

function adviceMetaText(page: AdviceScreenerPage): string {
  const n = pagePendingAdviceCount(page);
  const when = fmtAdviceWhen(cachedUpdatedAt);
  const parts = [`${n} advised`];
  if (when) parts.push(when);
  return parts.join(" · ");
}

function rememberAdvisorEnabled(agents: PaperAgentMeta[] | undefined): void {
  advisorEnabledKnown = true;
  cachedAdvisorEnabled = (agents || []).some((a) => a.kind === "advisor" && a.enabled);
}

function isAdviceRunAvailable(): boolean {
  if (overlayRun) return true;
  if (!advisorEnabledKnown) return false;
  return cachedAdvisorEnabled;
}

function bindAgentStatusListener(): void {
  if (agentStatusListenerBound) return;
  agentStatusListenerBound = true;
  window.addEventListener("sfe:agents-status-changed", () => {
    void refreshCachedAdvice().then(() => refreshAllBoundPanels());
  });
}

export function screenerRowIsAdvised(
  opts: {
    underlying: string;
    side: string;
    strike: number;
    expiration: string;
    optionSymbol?: string;
  },
  page: AdviceScreenerPage,
): boolean {
  return pageAdviceRecs(page).some((rec) =>
    attrsMatchRec(
      {
        underlying: opts.underlying,
        side: opts.side,
        strike: opts.strike,
        expiration: opts.expiration,
        optionSymbol: opts.optionSymbol,
      },
      rec,
    ),
  );
}

export function pinAdvisedScreenerRows<T>(
  allRows: T[],
  filtered: T[],
  page: AdviceScreenerPage,
  attrsOf: (row: T) => {
    underlying: string;
    side: string;
    strike: number;
    expiration: string;
    optionSymbol?: string;
  },
): T[] {
  if (!pageAdviceRecs(page).length) return filtered;
  const shown = new Set(filtered);
  const pinned = allRows.filter((row) => !shown.has(row) && screenerRowIsAdvised(attrsOf(row), page));
  return pinned.length ? [...pinned, ...filtered] : filtered;
}

export type AdviceScreenerSeed = {
  id: string;
  underlying: string;
  side: "call" | "put" | "";
  strike: number;
  expiration: string;
  optionSymbol: string;
  quantity: number;
  premium: number;
};

function dteFromExp(exp: string): number | undefined {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(exp)) return undefined;
  const [y, m, d] = exp.split("-").map(Number);
  const end = Date.UTC(y, (m || 1) - 1, d || 1);
  const now = new Date();
  const start = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  return Math.round((end - start) / 86_400_000);
}

/** Advisor opens that do not match any current screener row (need a synthetic line). */
export function unmatchedAdviceSeeds(
  page: AdviceScreenerPage,
  existing: Array<{
    underlying: string;
    side: string;
    strike: number;
    expiration: string;
    optionSymbol?: string;
  }>,
): AdviceScreenerSeed[] {
  return pageAdviceRecs(page)
    .filter((rec) => !existing.some((row) => attrsMatchRec(row, rec)))
    .map((rec) => {
      const { und, side, strike, exp, occ } = recFields(rec);
      return {
        id: rec.id,
        underlying: und,
        side,
        strike,
        expiration: exp,
        optionSymbol: occ,
        quantity: rec.quantity,
        premium: rec.premium,
      };
    })
    .filter((seed) => seed.underlying);
}

export function adviceSeedToScreenerRow(seed: AdviceScreenerSeed): {
  underlying_ticker: string;
  side: string;
  strike: number;
  expiration_date: string;
  option_symbol: string;
  dte?: number;
  mid?: number;
  last_price?: number;
  rec_quantity?: number;
  sfe_advice: true;
} {
  const premium = Number(seed.premium);
  const mid = Number.isFinite(premium) && premium > 0 ? premium : undefined;
  return {
    underlying_ticker: seed.underlying,
    side: seed.side,
    strike: seed.strike,
    expiration_date: seed.expiration,
    option_symbol: seed.optionSymbol,
    dte: dteFromExp(seed.expiration),
    mid,
    last_price: mid,
    rec_quantity: seed.quantity,
    sfe_advice: true,
  };
}

export function appendSyntheticAdviceRows<T>(
  visible: T[],
  matchAgainst: T[],
  page: AdviceScreenerPage,
  attrsOf: (row: T) => {
    underlying: string;
    side: string;
    strike: number;
    expiration: string;
    optionSymbol?: string;
  },
  toRow: (seed: AdviceScreenerSeed) => T,
): T[] {
  const seeds = unmatchedAdviceSeeds(page, matchAgainst.map(attrsOf));
  if (!seeds.length) return visible;
  return [...seeds.map(toRow), ...visible];
}

export function adviceBadgeHtml(): string {
  return `<span class="sfe-ai-advice-badge" title="Advisor recommendation">AI advice</span>`;
}

export function applyAdviceMarks(root: HTMLElement, page: AdviceScreenerPage): void {
  const rows = root.querySelectorAll<HTMLElement>("tbody tr");
  for (const row of rows) {
    clearAdviceDot(row);
    const hits = visibleAdviceRecs(cachedRecs).filter(
      (rec) => recMatchesPage(rec, page) && rowMatchesRec(row, rec),
    );
    if (!hits.length) continue;
    placeAdviceDot(row);
  }
}

function pruneBoundPanels(): void {
  for (let i = boundPanels.length - 1; i >= 0; i--) {
    if (!boundPanels[i].root.isConnected) boundPanels.splice(i, 1);
  }
}

function registerBoundPanel(root: HTMLElement, page: AdviceScreenerPage): void {
  pruneBoundPanels();
  const existing = boundPanels.find((p) => p.root === root);
  if (existing) {
    existing.page = page;
    return;
  }
  boundPanels.push({ root, page });
}

function refreshAllBoundPanels(): void {
  pruneBoundPanels();
  for (const panel of boundPanels) {
    applyAdviceMarks(panel.root, panel.page);
    paintAdviceButton(panel.root, panel.page);
  }
}

async function pickAdvisor(agents: PaperAgentMeta[]): Promise<string | null> {
  const advisors = (agents || []).filter((a) => a.kind === "advisor" && a.enabled);
  if (!advisors.length) return null;
  if (advisors.length === 1) return advisors[0].id;
  return choiceDialog({
    title: "Run agent advice",
    message: "Choose which advisor to run against your live portfolio. Recommendations are not executed.",
    options: advisors.map((a) => ({
      id: a.id,
      label: a.name,
      hint: "On",
    })),
  });
}

function adviceCacheSignature(recs: AdvisorRecommendation[], updatedAt: string | null): string {
  return `${updatedAt || ""}::${recs.map((r) => `${r.id}:${r.userStatus || "pending"}`).join(",")}`;
}

async function refreshCachedAdvice(): Promise<LatestAdviceResponse | null> {
  try {
    const latest = await fetchLatestAdvice();
    rememberAdvisorEnabled(latest.agents);
    cachedRecs = latest.recommendations || [];
    cachedUpdatedAt = pickLatestAdviceAt(latest);
    const sig = adviceCacheSignature(cachedRecs, cachedUpdatedAt);
    if (sig !== lastAdviceSig) {
      lastAdviceSig = sig;
      queueMicrotask(() => window.dispatchEvent(new CustomEvent(ADVICE_UPDATED_EVENT)));
    }
    return latest;
  } catch {
    return null;
  }
}

const IDLE_LABEL = "Run agent advice";
const INFLIGHT_EVENT = "sfe-paper-agent-inflight";

/** Survives Options/Puts/Calls panel remounts while POST /run-advice is in flight. */
let overlayRun: { agentId: string; startedAt?: string } | null = null;
let overlayPollTimer: ReturnType<typeof setInterval> | null = null;

function publishOverlayInflight(): void {
  window.dispatchEvent(
    new CustomEvent(INFLIGHT_EVENT, {
      detail: overlayRun
        ? { agentId: overlayRun.agentId, mode: "advice", startedAt: overlayRun.startedAt || null }
        : { agentId: null, mode: null, startedAt: null },
    }),
  );
}

function adviceButton(root: HTMLElement): HTMLButtonElement | null {
  return root.querySelector<HTMLButtonElement>("[data-sfe-run-advice]");
}

function paintAdviceButton(
  root: HTMLElement,
  page: AdviceScreenerPage,
  opts?: { label?: string; disabled?: boolean; error?: boolean },
): void {
  paintAdvisoryToggle(root, page);
  const btn = adviceButton(root);
  const canRun = isAdviceRunAvailable();
  const busy = Boolean(overlayRun) && !opts?.error;
  const showRun = canRun || Boolean(opts?.error);

  if (!btn) return;
  btn.hidden = !showRun;
  if (!showRun) return;

  const labelEl = btn.querySelector<HTMLElement>("[data-sfe-advice-label]");
  const metaEl = btn.querySelector<HTMLElement>("[data-sfe-advice-meta]");
  const label = opts?.label || (busy ? "Advising…" : IDLE_LABEL);
  if (labelEl) labelEl.textContent = label;
  else btn.textContent = label;
  btn.disabled = opts?.disabled ?? busy;
  if (!metaEl) return;
  if (opts?.error) {
    metaEl.hidden = true;
    metaEl.textContent = "";
    return;
  }
  if (busy) {
    const started = fmtAdviceWhen(overlayRun?.startedAt);
    metaEl.hidden = !started;
    metaEl.textContent = started ? `started ${started}` : "";
    return;
  }
  const meta = adviceMetaText(page);
  metaEl.hidden = !meta;
  metaEl.textContent = meta;
}

function stopOverlayPoll(): void {
  if (!overlayPollTimer) return;
  window.clearInterval(overlayPollTimer);
  overlayPollTimer = null;
}

function finishOverlayRun(errText?: string): void {
  overlayRun = null;
  stopOverlayPoll();
  publishOverlayInflight();
  refreshAllBoundPanels();
  if (!errText) return;
  for (const panel of boundPanels) {
    paintAdviceButton(panel.root, panel.page, { label: errText, disabled: true, error: true });
  }
  window.setTimeout(() => refreshAllBoundPanels(), 2200);
}

function startOverlayPoll(agentId: string, immediate = false): void {
  stopOverlayPoll();
  const tick = async (): Promise<void> => {
    const latest = await refreshCachedAdvice();
    if (!latest) {
      refreshAllBoundPanels();
      return;
    }
    const busy = (latest.inflight || []).find((row) => row.agentId === agentId);
    if (busy) {
      overlayRun = { agentId, startedAt: busy.startedAt };
      refreshAllBoundPanels();
      return;
    }
    finishOverlayRun();
  };
  if (immediate) void tick();
  overlayPollTimer = window.setInterval(() => void tick(), 3000);
}

async function runAdviceClick(root: HTMLElement, page: AdviceScreenerPage): Promise<void> {
  const btn = adviceButton(root);
  if (!btn || btn.disabled) return;
  paintAdviceButton(root, page, { label: "Running…", disabled: true });
  try {
    const latest = await refreshCachedAdvice();
    refreshAllBoundPanels();
    const agents = latest?.agents || [];
    const inflight = latest?.inflight || [];
    const advisors = agents.filter((a) => a.kind === "advisor");
    if (!advisors.length) {
      paintAdviceButton(root, page, { label: "No advisor", disabled: true, error: true });
      window.setTimeout(() => refreshAllBoundPanels(), 1800);
      return;
    }
    const agentId = await pickAdvisor(advisors);
    if (!agentId) {
      refreshAllBoundPanels();
      return;
    }
    const already =
      overlayRun?.agentId === agentId || inflight.some((row) => row.agentId === agentId);
    const startedAt = inflight.find((row) => row.agentId === agentId)?.startedAt;
    overlayRun = { agentId, startedAt };
    publishOverlayInflight();
    refreshAllBoundPanels();
    startOverlayPoll(agentId, already);
    if (already) return;
    try {
      const body = await runAgentAdvice(agentId);
      const after = await refreshCachedAdvice();
      const still = (after?.inflight || []).some((row) => row.agentId === agentId);
      const skippedRunning =
        still ||
        (body &&
          typeof body === "object" &&
          "result" in body &&
          (body as { result?: { skipped?: boolean; reason?: string } }).result?.reason ===
            "already_running");
      if (skippedRunning) {
        startOverlayPoll(agentId, true);
        return;
      }
      finishOverlayRun();
    } catch (err) {
      finishOverlayRun(err instanceof Error ? err.message.slice(0, 28) : "Failed");
    }
  } catch (err) {
    paintAdviceButton(root, page, {
      label: err instanceof Error ? err.message.slice(0, 28) : "Failed",
      disabled: true,
      error: true,
    });
    window.setTimeout(() => refreshAllBoundPanels(), 2200);
  }
}

export function bindScreenerAdvice(root: HTMLElement, page: AdviceScreenerPage): void {
  bindAgentStatusListener();
  root.dataset.sfeAdvicePage = page;
  registerBoundPanel(root, page);
  applyAdviceMarks(root, page);
  if (overlayRun) {
    paintAdviceButton(root, page);
  } else {
    paintAdviceButton(root, page);
    void refreshCachedAdvice().then((latest) => {
      applyAdviceMarks(root, page);
      const busy = (latest?.inflight || [])[0];
      if (busy) {
        overlayRun = { agentId: busy.agentId, startedAt: busy.startedAt };
        publishOverlayInflight();
        startOverlayPoll(busy.agentId, true);
      }
      refreshAllBoundPanels();
    });
  }
  if (root.dataset.sfeAdviceBound === "1") return;
  root.dataset.sfeAdviceBound = "1";
  root.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    const toggle = target?.closest<HTMLElement>("[data-sfe-advisory-toggle]");
    if (toggle && root.contains(toggle)) {
      e.preventDefault();
      e.stopPropagation();
      setAdvisoryVisible(!isAdvisoryVisible());
      refreshAllBoundPanels();
      // Puts/Calls re-run their filters on this event to drop or restore advisor rows.
      window.dispatchEvent(new CustomEvent(ADVICE_UPDATED_EVENT));
      return;
    }
    const btn = target?.closest<HTMLButtonElement>("[data-sfe-run-advice]");
    if (btn && root.contains(btn)) {
      e.preventDefault();
      const current = (root.dataset.sfeAdvicePage as AdviceScreenerPage | undefined) || page;
      void runAdviceClick(root, current);
      return;
    }
    const dot = target?.closest<HTMLElement>(".sfe-advice-dot");
    if (!dot || !root.contains(dot)) return;
    e.preventDefault();
    e.stopPropagation();
    const row = dot.closest("tr");
    if (!(row instanceof HTMLElement)) return;
    const current = (root.dataset.sfeAdvicePage as AdviceScreenerPage | undefined) || page;
    const hits = visibleAdviceRecs(cachedRecs).filter(
      (rec) => recMatchesPage(rec, current) && rowMatchesRec(row, rec),
    );
    if (hits.length) openAdviceModal(hits);
  });
}
